import { NetworkStatus, QueryHookOptions, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import { AnyToken } from 'src/common/types/models/Tokens'
import { mapBy } from 'src/common/utils/collections/mapBy'
import { TokensQuery } from '../queries/swarmx/TokensQuery'
import {
  TokensQuery as TokensQueryGQL,
  TokensQueryVariables,
} from '../types/swarmx/graphql'
import { normalizeAllERC20s, normalizeAllNFTs } from '../utils/dotc'

const useTokens = (
  options?: QueryHookOptions<TokensQueryGQL, TokensQueryVariables>
) => {
  const { data, loading, error, fetchMore, networkStatus, refetch } = useQuery(
    TokensQuery,
    {
      fetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
      ...options,
      variables: {
        skip: 0,
        ...options?.variables,
        filter: {
          ...options?.variables?.filter,
          id_in: [
            '0x3c499c542cef5e3811e1192ce70d8cc03d5c3359',
            '0x0AAA36AD5DB4f4ea173B40A9428529ebAD73Fc89',
          ].map(a => a.toLowerCase()),
        },
      },
    }
  )

  const ERC20s = useMemo(() => {
    return normalizeAllERC20s(data?.assets || [])
  }, [data?.assets])

  const NFTs = useMemo(() => {
    return normalizeAllNFTs(data?.assets || [])
  }, [data?.assets])

  const tokensDictionary = mapBy(
    [...ERC20s, ...NFTs],
    'contractAddress'
  ) as Map<string, AnyToken>

  return useMemo(
    () => ({
      erc20s: ERC20s,
      nfts: NFTs,
      tokensDictionary,
      loading:
        loading ||
        ![
          NetworkStatus.ready,
          NetworkStatus.error,
          NetworkStatus.poll,
        ].includes(networkStatus),
      error,
      refetch,
      refetching: [NetworkStatus.poll].includes(networkStatus),
      fetchMore: fetchMore,
    }),
    [
      ERC20s,
      NFTs,
      error,
      fetchMore,
      loading,
      networkStatus,
      refetch,
      tokensDictionary,
    ]
  )
}

export default useTokens
